import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { invitationAPI } from '../api';

function AcceptInvitation() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link');
      setLoading(false);
      return;
    }

    // Validate invitation token
    const validateInvitation = async () => {
      try {
        const response = await invitationAPI.validate(token);
        setInvitation(response.data);

        // If user doesn't exist, redirect to register
        if (!response.data.user_exists) {
          navigate(`/register?token=${token}`);
          return;
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Invalid or expired invitation');
      } finally {
        setLoading(false);
      }
    };

    validateInvitation();
  }, [token, navigate]);

  const handleAccept = async () => {
    if (!isAuthenticated) {
      // Store token and redirect to login
      navigate(`/login?redirect=/invite/${token}`);
      return;
    }

    try {
      setAccepting(true);
      setError('');
      const response = await invitationAPI.accept(token);

      // Redirect to the group
      navigate(`/groups/${response.data.group.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to accept invitation');
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h2>❌ Invalid Invitation</h2>
          <p className="error-message">{error}</p>
          <p>Please contact the group organizer for a new invitation.</p>
          <button onClick={() => navigate('/')} className="btn btn-primary" style={{ marginTop: '20px' }}>
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (!invitation) {
    return null;
  }

  return (
    <div className="auth-container">
      <div className="card">
        <h2>🎁 Group Invitation</h2>

        <div className="info-message">
          <strong>{invitation.group.organizer_name}</strong> invited you to join{' '}
          <strong>{invitation.group.name}</strong>
        </div>

        {invitation.group.description && (
          <p style={{ margin: '20px 0', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
            {invitation.group.description}
          </p>
        )}

        <div style={{ margin: '20px 0', color: '#666' }}>
          <p style={{ margin: '5px 0' }}>
            <strong>Organizer:</strong> {invitation.group.organizer_name}
          </p>
          <p style={{ margin: '5px 0' }}>
            <strong>Members:</strong> {invitation.group.member_count}
          </p>
          <p style={{ margin: '5px 0' }}>
            <strong>Your email:</strong> {invitation.invitation.email}
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}

        {!isAuthenticated ? (
          <div>
            <p style={{ margin: '20px 0' }}>
              Please login to accept this invitation.
            </p>
            <button
              onClick={() => navigate(`/login?redirect=/invite/${token}`)}
              className="btn btn-primary"
              style={{ width: '100%' }}
            >
              Login to Accept
            </button>
          </div>
        ) : (
          <div>
            {user && user.email.toLowerCase() !== invitation.invitation.email.toLowerCase() && (
              <div className="error-message" style={{ marginBottom: '20px' }}>
                You are logged in as <strong>{user.email}</strong>, but this invitation was sent to{' '}
                <strong>{invitation.invitation.email}</strong>. Please logout and login with the correct account.
              </div>
            )}

            <button
              onClick={handleAccept}
              className="btn btn-primary"
              disabled={accepting || (user && user.email.toLowerCase() !== invitation.invitation.email.toLowerCase())}
              style={{ width: '100%' }}
            >
              {accepting ? 'Joining Group...' : 'Accept Invitation'}
            </button>
          </div>
        )}

        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          <button onClick={() => navigate('/')} className="btn btn-secondary">
            Decline
          </button>
        </div>
      </div>
    </div>
  );
}

export default AcceptInvitation;
